const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const buildSupportPartnersAction = (label = 'Open Mission Support', tab = 'supporters', roomId = 'support-partners') => ({
  label,
  tab,
  roomId,
});

export function buildSupportPartnersRoomContext({
  room,
  supportOverview = null,
  primaryAction,
  secondaryAction,
}) {
  const stats = supportOverview?.stats || {};
  const supporters = Array.isArray(supportOverview?.supporters) ? supportOverview.supporters : [];
  const resendConfig = supportOverview?.resend_config || {};
  const pausedSupporter = supporters.find((supporter) => ['paused', 'inactive'].includes(String(supporter?.support_status || '').toLowerCase()));
  const failedSyncCount = Number(stats.failed_syncs || 0);
  const missingConfigCount = [
    resendConfig.has_resend_api_key,
    resendConfig.has_support_segment_id,
    resendConfig.has_monthly_segment_id,
    resendConfig.has_updates_topic_id,
    resendConfig.has_monthly_topic_id,
  ].filter((value) => !value).length;
  const unsubscribedCount = Number(stats.unsubscribed || 0);
  const subscriberOnlyCount = Number(stats.subscriber_only || 0);

  const priorityItems = [];

  if (pausedSupporter) {
    priorityItems.push({
      tone: 'attention',
      eyebrow: 'Do first',
      title: `${pausedSupporter.name || pausedSupporter.email || 'A supporter'} needs a personal check-in`,
      detail: 'A support relationship appears paused or inactive. Lead with care before this turns transactional or invisible.',
      location: 'In Mission Support',
    });
  }

  if (failedSyncCount > 0) {
    priorityItems.push({
      tone: pausedSupporter ? 'next' : 'attention',
      eyebrow: pausedSupporter ? 'Then' : 'Do first',
      title: `${pluralize(failedSyncCount, 'support sync')} need review`,
      detail: 'Support records lose trust quickly when sync status is unclear. Confirm what actually landed in Resend.',
      location: 'In Mission Support',
    });
  }

  if (missingConfigCount > 0) {
    priorityItems.push({
      tone: priorityItems.length ? 'steady' : 'next',
      eyebrow: priorityItems.length ? 'Keep tidy' : 'Do first',
      title: `${pluralize(missingConfigCount, 'Resend support setting')} still need configuration`,
      detail: 'Support communication should feel dependable, not improvised. Finish the missing wiring before pressure rises.',
      location: 'In Mission Support',
    });
  }

  if (!priorityItems.length) {
    priorityItems.push({
      tone: 'steady',
      eyebrow: 'Steady',
      title: 'Keep support relationships visible and warm',
      detail: 'Nothing urgent is surfacing right now, so stay close to supporter trust and the quality of follow-through.',
      location: 'In Mission Support',
    });
  }

  const statusTone = pausedSupporter || failedSyncCount > 0
    ? 'attention'
    : missingConfigCount > 0 || Number(stats.total_supporters || 0) > 0
      ? 'next'
      : 'steady';

  const statusLabel = pausedSupporter
    ? 'Needs care'
    : failedSyncCount > 0
      ? 'Needs review'
      : missingConfigCount > 0
        ? 'Tidy next'
        : Number(stats.total_supporters || 0) > 0
          ? 'In motion'
          : 'Ready';

  return {
    id: room.id,
    variant: 'support-partners',
    statusTone,
    statusLabel,
    startTitle: pausedSupporter
      ? 'Start with trust that may be softening'
      : failedSyncCount > 0
        ? 'Settle support trust before the data drifts'
        : 'Keep mission support relational, visible, and dependable',
    startDetail: 'This room should help you see who is supporting the mission, where follow-through is drifting, and which personal care move keeps support from becoming mechanical.',
    signals: [
      {
        tone: pausedSupporter ? 'attention' : 'steady',
        text: pausedSupporter
          ? `${pausedSupporter.name || pausedSupporter.email || 'A supporter'} appears paused and may need a gracious check-in.`
          : 'No paused supporter relationships are surfaced right now.',
      },
      {
        tone: failedSyncCount > 0 ? 'attention' : 'steady',
        text: failedSyncCount > 0
          ? `${pluralize(failedSyncCount, 'support sync')} currently need review.`
          : 'Support sync health is currently quiet.',
      },
      {
        tone: subscriberOnlyCount > 0 ? 'next' : 'steady',
        text: subscriberOnlyCount > 0
          ? `${pluralize(subscriberOnlyCount, 'support update subscriber')} are watching without giving yet.`
          : 'No subscriber-only support records are currently surfaced.',
      },
    ],
    metrics: [
      {
        label: 'Total supporters',
        value: Number(stats.total_supporters || 0),
        detail: 'People currently in the mission support system',
        tone: Number(stats.total_supporters || 0) > 0 ? 'next' : 'steady',
      },
      {
        label: 'Failed syncs',
        value: failedSyncCount,
        detail: 'Support records needing Resend trust review',
        tone: failedSyncCount > 0 ? 'attention' : 'steady',
      },
      {
        label: 'Unsubscribed',
        value: unsubscribedCount,
        detail: 'Supporters who no longer want updates',
        tone: unsubscribedCount > 0 ? 'next' : 'steady',
      },
    ],
    priorityItems,
    watchListTitle: 'Support watchlist',
    watchListItems: [
      subscriberOnlyCount > 0
        ? {
            title: `${pluralize(subscriberOnlyCount, 'subscriber-only record')} are still waiting for a fuller relationship`,
            detail: 'These people care enough to stay connected, but are not yet giving financially.',
            tone: 'next',
          }
        : null,
      missingConfigCount > 0
        ? {
            title: `${pluralize(missingConfigCount, 'Resend support setting')} remain unset`,
            detail: 'Finish the missing wiring before a support push depends on it.',
            tone: 'steady',
          }
        : null,
    ].filter(Boolean),
    primaryAction: primaryAction || buildSupportPartnersAction(),
    secondaryAction: secondaryAction || null,
  };
}
